/**
 * @file functions/setOrderFulfillment.ts
 * 
 * Utility function to set the fulfillment method for an order.
 *
 * @param {OrderState} orderState - The current state of the order.
 * @param {FulfillmentMethod} method - The fulfillment method.
 * @param {string | number} storeNumber - The store number.
 * @param {string} [fulfillmentDateTime] - The pickup date/time (optional).
 * 
 * @returns {Promise<object>} - The result of the fulfillment update.
 */

// 3rd Party Imports
import { getSession } from "next-auth/react";

// Function Imports
import { addAuthorizationHeader } from "@/lib/functions/addAuthorizationHeader";
import { getOrderToken } from "@/lib/functions/getOrderToken";

// Type Imports
import type { FulfillmentMethod } from "@/lib/types/fulfillmentMethod";
import type { OrderState as OrderStateType } from "@/lib/types/orderState";
import type { OrderActions } from "@/lib/context/orderActions";

export async function setOrderFulfillment(
  orderToken: string | null | undefined,
  orderTokenType: 'customer' | 'guest',
  orderState: OrderStateType,
  orderDispatch: React.Dispatch<OrderActions>,
  method: FulfillmentMethod,
  storeNumber?: string | number,
  fulfillmentDateTime?: string,
) {

  try {
    // Fetch/refresh token when missing
    let effectiveToken = orderToken;
    if (!effectiveToken || effectiveToken == null) {
      const tokenResp = await getOrderToken(orderState, orderDispatch);
      if( tokenResp.okay && tokenResp.token) {
        effectiveToken = tokenResp?.token;
      }
    }

    // Handle missing order token
    if (!effectiveToken) {
      return { success: false, message: 'Order token is required' };
    }

    // Get the session to check if the user is logged in
    const session = await getSession();

    // Prepare headers for the request
    const headers = addAuthorizationHeader(
      { 'Content-Type': 'application/json' },
      orderTokenType == 'customer' ? session?.token : null,
    );

    // Update fulfillment method
    const fulfillmentBody = JSON.stringify(
      Object.fromEntries(
        Object.entries({
          method,
          store_number: storeNumber,
          fulfillment_date_time: fulfillmentDateTime,
        }).filter(([, v]) => v !== undefined)
      )
    );

    const resp = await fetch(`/api/orders/${effectiveToken}/fulfillment/`, {
      method: 'POST',
      headers,
      body: fulfillmentBody,
      next: {
        revalidate: 0,
      },
    });

    if (resp?.status === 200) {
      const data = await resp.json();
      return { success: true, message: '', orderData: data };
    } else {
      const data = await resp.json();
      return { success: false, message: data.message || 'Unspecified error' };
    }
  } catch {
    return { success: false, message: 'Internal Server Error' };
  }
}
